/**
 * Team Absence Calendar Hook
 *
 * Supplies the manager's team absence data for the "Manager Absence Calendar"
 * tab, shaped for the CalendarRow grid: one entry per employee, each holding a
 * list of per-day leave entries within the selected date range.
 *
 * Rather than issue new API calls, this derives from `useTeamLeaveRequests`,
 * which already sources exactly the entities the calendar needs:
 *   - the team roster from EssWorkerManagerRelations (via useTeamMembers),
 *   - submitted leave requests + per-day lines from LeaveRequestsV2, and
 *   - the authoritative status per request from EssLeaveRequestHeaders.
 * (LeaveRequestHeaders / notes are surfaced through the same request records —
 * `reason` carries the leave note.)
 *
 * Shape returned per member (matches the previous mockTeamAbsence contract that
 * CalendarRow consumes):
 *   { id, personnelNumber, name, entries: [{ date, amount, unit, type, status, requestId, reason }] }
 * where `date` is a local 'YYYY-MM-DD' string, `amount` is the per-day value,
 * and `unit` ('Days' | 'Hours') comes from the leave type's LeaveAmountUnit so
 * CalendarRow can label it correctly (a day-unit leave must not read as hours).
 *
 * @param {{ start: string, end: string }} range - Applied 'YYYY-MM-DD' range.
 *
 * Returns:
 *   - teamAbsence: Array<member>
 *   - isLoading: boolean
 *   - error: Error | null
 */
import { useMemo } from "react";
import useTeamLeaveRequests from "./useTeamLeaveRequests";

/** Local YYYY-MM-DD key for a date string, avoiding UTC shift. */
const toLocalKey = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function useTeamAbsenceCalendar(range) {
  const { requests, isLoading, error } = useTeamLeaveRequests();

  const rangeStart = range?.start || null;
  const rangeEnd = range?.end || null;

  const teamAbsence = useMemo(() => {
    // Group every request's leave lines by employee, keeping only lines that
    // fall inside the applied range.
    const byEmployee = new Map();

    requests.forEach((req) => {
      const key = req.employeeNo;
      if (!byEmployee.has(key)) {
        byEmployee.set(key, {
          id: req.employeeNo,
          personnelNumber: req.employeeNo,
          name: req.employeeName || req.employeeNo,
          entries: [],
        });
      }
      const member = byEmployee.get(key);

      (req.leaveLines || []).forEach((line) => {
        const dateKey = toLocalKey(line.date);
        if (!dateKey) return;
        if (rangeStart && dateKey < rangeStart) return;
        if (rangeEnd && dateKey > rangeEnd) return;

        member.entries.push({
          date: dateKey,
          amount: typeof line.amount === "number" ? line.amount : 0,
          // Unit comes from the leave type (LeaveAmountUnit): 'Days' or 'Hours'.
          unit: req.unit === "Hours" ? "Hours" : "Days",
          type: req.leaveTypeName || req.leaveTypeCode || "Leave",
          status: req.status || "Unknown",
          requestId: req.RequestId,
          reason: req.reason || "",
        });
      });
    });

    // Sort each member's entries chronologically; drop members with no leave in
    // range so the calendar only lists people who are actually absent.
    return Array.from(byEmployee.values())
      .map((m) => ({
        ...m,
        entries: m.entries.sort((a, b) => a.date.localeCompare(b.date)),
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [requests, rangeStart, rangeEnd]);

  return {
    teamAbsence,
    isLoading,
    error: error || null,
  };
}
